/**
 * C.15 — NIPE topic tile grid.
 *
 * Styled tile grid (Q17 user note — NOT a "wall of unformed text").
 * Operator-only tiles filtered via useRepancyRole. Each tile fires
 * onSelectTopic with the topic key; the parent (FirstVisitOverlay) is
 * responsible for displaying the topic body.
 */

import { useState, type ReactElement } from 'react';
import { testIds } from '../../lib/testIds';
import { NIPE_TOPICS, type NipeTopic } from './topics/index';
import './NipeTopicTileGrid.css';

/**
 * Pull the first lead sentence from a topic's body for the hover
 * tooltip. Topic bodies are paragraph-separated markdown — the first
 * paragraph is always the lead sentence (everything after is bullet
 * detail). Strip the trivial markdown (bold/italic) and return the
 * leading sentence so hover reveals real content, not a redundant
 * echo of the visible label + blurb.
 *
 * Returns null when the body has nothing to preview — callers should
 * skip the `title=` attribute entirely in that case (silent is better
 * than redundant).
 */
function previewFromBody(body: string | undefined): string | null {
  if (!body) return null;
  const firstParagraph = body.split(/\n\s*\n/)[0]?.trim();
  if (!firstParagraph) return null;
  // Strip the most common markdown noise. We intentionally don't pull
  // in a full markdown→plaintext library here — topic bodies are
  // hand-written and consistent.
  const stripped = firstParagraph
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!stripped) return null;
  // Cap length so the native tooltip stays scannable.
  if (stripped.length <= 220) return stripped;
  return stripped.slice(0, 217).trimEnd() + '…';
}

export interface NipeTopicTileGridProps {
  isOperator: boolean;
  onSelectTopic?: (topic: NipeTopic) => void;
}

export function NipeTopicTileGrid({
  isOperator,
  onSelectTopic,
}: NipeTopicTileGridProps): ReactElement {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // Top-tier filter trims the 10-tile catalog down to the 4 lawyer-
  // first topics shown in the chat panel empty state. Non-top-tier
  // topics stay in NIPE_TOPICS so direct callers (NIPE tools, future
  // surfaces) can still resolve them.
  const visible = NIPE_TOPICS
    .filter((t) => t.topTier === true)
    .filter((t) => (t.operatorOnly ? isOperator : true));
  const active = visible.find((t) => t.key === activeKey) ?? null;

  return (
    <div className="nipe-topic-tile-wrap">
      <div className="nipe-topic-tile-grid" data-testid={testIds.nipe.topicTileGrid}>
        {visible.map((topic) => {
          const isAction = topic.action === 'open-window';
          const className = [
            'nipe-topic-tile',
            isAction ? 'nipe-topic-tile-action' : '',
            activeKey === topic.key ? 'active' : '',
          ].filter(Boolean).join(' ');
          return (
            <button
              key={topic.key}
              type="button"
              data-testid={testIds.nipe.topicTile(topic.key)}
              className={className}
              // Hover tooltip = lead sentence of the topic body, NOT a
              // re-echo of the visible label+blurb. If the topic has
              // no body worth previewing (e.g. action tiles whose body
              // is just a CTA blurb), drop the title attribute entirely.
              {...(previewFromBody(topic.body)
                ? { title: previewFromBody(topic.body) as string }
                : {})}
              onClick={() => {
                setActiveKey(topic.key);
                onSelectTopic?.(topic);
              }}
            >
              <span className="nipe-topic-tile-icon" aria-hidden="true">
                {topic.iconSrc ? (
                  <img src={topic.iconSrc} alt="" width={24} height={24} style={{ imageRendering: 'pixelated' }} />
                ) : topic.icon}
              </span>
              <span className="nipe-topic-tile-label">
                {topic.label}
                {isAction ? <span aria-hidden> ↗</span> : null}
              </span>
              <span className="nipe-topic-tile-blurb">{topic.blurb}</span>
            </button>
          );
        })}
      </div>
      {active ? (
        <div className="nipe-topic-tile-body">
          <div className="nipe-topic-tile-body-title">
            {active.iconSrc ? (
              <img src={active.iconSrc} alt="" width={16} height={16} style={{ imageRendering: 'pixelated', verticalAlign: 'text-bottom', marginRight: 4 }} />
            ) : active.icon}{' '}{active.label}
          </div>
          <pre className="nipe-topic-tile-body-content">{active.body}</pre>
        </div>
      ) : null}
    </div>
  );
}
